import { useState, useEffect } from "react";
import { useHistory, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import useHttp from "../../hooks/use-http";
import { endPoints } from "../../config/end-points";
import { projectActions } from "../../store/project-store";
import { authActions } from "../../store/auth";
import { cuteToast } from "../UI/cute";
import SitemapCanvas from "./canvas/SitemapCanvas";
import { FiArrowLeft, FiArrowRight, FiZap, FiLoader, FiCheck, FiGlobe, FiEdit3, FiImage, FiAlignLeft, FiSearch, FiChevronDown, FiEye, FiX } from "react-icons/fi";
import { THEMES } from "../../data/component-templates/themes";
import "./projectcreate.css";

const NICHE_GROUPS = [
  {
    label: "Food & Hospitality",
    niches: [
      { value: "restaurant", label: "Restaurant / Cafe / Bakery" },
      { value: "travel", label: "Travel / Hospitality / Hotel" },
    ]
  },
  {
    label: "Health & Wellness",
    niches: [
      { value: "dental", label: "Dental Practice" },
      { value: "medical", label: "Healthcare / Clinic" },
      { value: "salon", label: "Salon / Spa / Wellness" },
      { value: "gym", label: "Gym / Fitness" },
    ]
  },
  {
    label: "Home & Trade Services",
    niches: [
      { value: "plumbing", label: "Plumbing / Home Services" },
      { value: "roofing", label: "Roofing / Construction" },
      { value: "cleaning", label: "Cleaning Services" },
      { value: "automotive", label: "Automotive / Mechanic" },
    ]
  },
  {
    label: "Professional Services",
    niches: [
      { value: "legal", label: "Legal / Law Firm" },
      { value: "realestate", label: "Real Estate" },
      { value: "accounting", label: "Accounting / Finance" },
      { value: "coaching", label: "Coaching / Consulting" },
    ]
  },
  {
    label: "Technology & Creative",
    niches: [
      { value: "saas", label: "Technology / SaaS" },
      { value: "agency", label: "Agency / Creative Studio" },
      { value: "photography", label: "Photography / Videography" },
    ]
  },
  {
    label: "Community & Education",
    niches: [
      { value: "education", label: "Education / Training" },
      { value: "nonprofit", label: "Nonprofit / Charity" },
      { value: "church", label: "Church / Religious" },
    ]
  },
  {
    label: "Retail & Events",
    niches: [
      { value: "ecommerce", label: "Ecommerce / Online Store" },
      { value: "pet", label: "Pet Services" },
      { value: "events", label: "Event Planning / Weddings" },
    ]
  },
  {
    label: "Other",
    niches: [
      { value: "general", label: "General Business" },
    ]
  }
];

// Flat list for backward compatibility
const NICHES = NICHE_GROUPS.flatMap(g => g.niches);

const PAGE_NAMES_BY_NICHE = {
  restaurant: { 1: ["Home"], 2: ["Home", "Contact"], 3: ["Home", "Menu", "Contact"], 4: ["Home", "Menu", "About", "Contact"], 5: ["Home", "Menu", "About", "Gallery", "Contact"] },
  travel:     { 1: ["Home"], 2: ["Home", "Contact"], 3: ["Home", "Destinations", "Contact"], 4: ["Home", "Destinations", "About", "Contact"], 5: ["Home", "Destinations", "About", "Gallery", "Contact"] },
  dental:     { 1: ["Home"], 2: ["Home", "Contact"], 3: ["Home", "Services", "Contact"], 4: ["Home", "Services", "About", "Contact"], 5: ["Home", "Services", "About", "Team", "Contact"] },
  medical:    { 1: ["Home"], 2: ["Home", "Contact"], 3: ["Home", "Services", "Contact"], 4: ["Home", "Services", "About", "Contact"], 5: ["Home", "Services", "About", "Team", "Contact"] },
  salon:      { 1: ["Home"], 2: ["Home", "Contact"], 3: ["Home", "Services", "Contact"], 4: ["Home", "Services", "Gallery", "Contact"], 5: ["Home", "Services", "Gallery", "About", "Contact"] },
  gym:        { 1: ["Home"], 2: ["Home", "Contact"], 3: ["Home", "Classes", "Contact"], 4: ["Home", "Classes", "Trainers", "Contact"], 5: ["Home", "Classes", "Trainers", "Pricing", "Contact"] },
  plumbing:   { 1: ["Home"], 2: ["Home", "Contact"], 3: ["Home", "Services", "Contact"], 4: ["Home", "Services", "About", "Contact"], 5: ["Home", "Services", "About", "FAQ", "Contact"] },
  roofing:    { 1: ["Home"], 2: ["Home", "Contact"], 3: ["Home", "Services", "Contact"], 4: ["Home", "Services", "About", "Contact"], 5: ["Home", "Services", "About", "Gallery", "Contact"] },
  cleaning:   { 1: ["Home"], 2: ["Home", "Contact"], 3: ["Home", "Services", "Contact"], 4: ["Home", "Services", "About", "Contact"], 5: ["Home", "Services", "About", "Pricing", "Contact"] },
  automotive: { 1: ["Home"], 2: ["Home", "Contact"], 3: ["Home", "Services", "Contact"], 4: ["Home", "Services", "About", "Contact"], 5: ["Home", "Services", "About", "Gallery", "Contact"] },
  legal:      { 1: ["Home"], 2: ["Home", "Contact"], 3: ["Home", "Practice Areas", "Contact"], 4: ["Home", "Practice Areas", "About", "Contact"], 5: ["Home", "Practice Areas", "About", "Team", "Contact"] },
  realestate: { 1: ["Home"], 2: ["Home", "Contact"], 3: ["Home", "Properties", "Contact"], 4: ["Home", "Properties", "About", "Contact"], 5: ["Home", "Properties", "About", "Team", "Contact"] },
  accounting: { 1: ["Home"], 2: ["Home", "Contact"], 3: ["Home", "Services", "Contact"], 4: ["Home", "Services", "About", "Contact"], 5: ["Home", "Services", "About", "Team", "Contact"] },
  coaching:   { 1: ["Home"], 2: ["Home", "Contact"], 3: ["Home", "Programs", "Contact"], 4: ["Home", "Programs", "About", "Contact"], 5: ["Home", "Programs", "About", "Testimonials", "Contact"] },
  saas:       { 1: ["Home"], 2: ["Home", "Contact"], 3: ["Home", "Features", "Contact"], 4: ["Home", "Features", "Pricing", "Contact"], 5: ["Home", "Features", "Pricing", "About", "Contact"] },
  agency:     { 1: ["Home"], 2: ["Home", "Contact"], 3: ["Home", "Services", "Contact"], 4: ["Home", "Services", "Portfolio", "Contact"], 5: ["Home", "Services", "Portfolio", "About", "Contact"] },
  photography:{ 1: ["Home"], 2: ["Home", "Contact"], 3: ["Home", "Portfolio", "Contact"], 4: ["Home", "Portfolio", "About", "Contact"], 5: ["Home", "Portfolio", "About", "Pricing", "Contact"] },
  education:  { 1: ["Home"], 2: ["Home", "Contact"], 3: ["Home", "Courses", "Contact"], 4: ["Home", "Courses", "About", "Contact"], 5: ["Home", "Courses", "About", "Team", "Contact"] },
  nonprofit:  { 1: ["Home"], 2: ["Home", "Contact"], 3: ["Home", "Mission", "Contact"], 4: ["Home", "Mission", "Programs", "Contact"], 5: ["Home", "Mission", "Programs", "Team", "Contact"] },
  church:     { 1: ["Home"], 2: ["Home", "Contact"], 3: ["Home", "About", "Contact"], 4: ["Home", "About", "Ministries", "Contact"], 5: ["Home", "About", "Ministries", "Events", "Contact"] },
  ecommerce:  { 1: ["Home"], 2: ["Home", "Contact"], 3: ["Home", "Products", "Contact"], 4: ["Home", "Products", "About", "Contact"], 5: ["Home", "Products", "About", "FAQ", "Contact"] },
  pet:        { 1: ["Home"], 2: ["Home", "Contact"], 3: ["Home", "Services", "Contact"], 4: ["Home", "Services", "About", "Contact"], 5: ["Home", "Services", "About", "Gallery", "Contact"] },
  events:     { 1: ["Home"], 2: ["Home", "Contact"], 3: ["Home", "Services", "Contact"], 4: ["Home", "Services", "Gallery", "Contact"], 5: ["Home", "Services", "Gallery", "About", "Contact"] },
  general:    { 1: ["Home"], 2: ["Home", "Contact"], 3: ["Home", "About", "Contact"], 4: ["Home", "About", "Services", "Contact"], 5: ["Home", "About", "Services", "Gallery", "Contact"] },
};

const ProjectCreate = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { sendRequest } = useHttp();

  const { currentProject, sitemap, generationStep } = useSelector((state) => state.project);
  const token = useSelector((state) => state.auth.token);

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    niche: "general",
    no_of_pages: 3,
    theme: "classic",
    desc_what: "",
    desc_services: "",
    desc_location: "",
    desc_differentiator: "",
    desc_contact: "",
    knowledge_base: "",
  });
  const [showKnowledgeBase, setShowKnowledgeBase] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [viewMode, setViewMode] = useState("text");
  const [errors, setErrors] = useState({});
  const [loadingState, setLoadingState] = useState({
    show: false,
    step: 0,
    message: "",
    subMessage: ""
  });
  const [inputMode, setInputMode] = useState("manual"); // "manual" | "ai-autofill"
  const [analyzeUrl, setAnalyzeUrl] = useState("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState(null);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [usedFallback, setUsedFallback] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [nicheDropdownOpen, setNicheDropdownOpen] = useState(false);
  const [nicheSearch, setNicheSearch] = useState("");
  const [themePreview, setThemePreview] = useState(null);

  useEffect(() => {
    document.title = `${endPoints.title} | Create Website`;
    // Clear any previous project data
    dispatch(projectActions.clearProject());
    return () => {
      dispatch(projectActions.setGenerationStep("idle"));
    };
  }, [dispatch]);

  // Close niche dropdown on outside click
  useEffect(() => {
    if (!nicheDropdownOpen) return;
    const handleClick = (e) => {
      if (!e.target.closest('.pc-field')) setNicheDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [nicheDropdownOpen]);

  const mergeDescription = () => {
    const parts = [];
    if (formData.desc_what.trim()) parts.push(formData.desc_what.trim());
    if (formData.desc_services.trim()) parts.push(`Services: ${formData.desc_services.trim()}`);
    if (formData.desc_location.trim()) parts.push(`Location: ${formData.desc_location.trim()}`);
    if (formData.desc_differentiator.trim()) parts.push(`Unique: ${formData.desc_differentiator.trim()}`);
    if (formData.desc_contact.trim()) parts.push(`Contact: ${formData.desc_contact.trim()}`);
    return parts.join('\n\n');
  };

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = "Project name is required";
    }
    if (!formData.desc_what.trim()) {
      newErrors.desc_what = "Please describe what your business does";
    } else if (formData.desc_what.trim().length < 30) {
      newErrors.desc_what = "Please add a bit more detail (at least 30 characters)";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleEnhanceDescription = () => {
    const merged = mergeDescription();
    if (merged.length < 30) {
      cuteToast({ type: "error", message: "Please fill in the business details above first (at least 30 characters)." });
      return;
    }
    setIsEnhancing(true);
    sendRequest(
      { url: endPoints.projectEnhanceDescription, method: "POST", body: { description: merged, niche: formData.niche } },
      (res) => {
        setIsEnhancing(false);
        if (res.status === 200 && res.body?.knowledge_base) {
          setFormData(prev => ({ ...prev, knowledge_base: res.body.knowledge_base }));
          setShowKnowledgeBase(true);
          cuteToast({ type: "success", message: "Knowledge base generated! Review and edit as needed." });
        } else {
          cuteToast({ type: "error", message: res.message || "Failed to enhance description" });
        }
      }
    );
  };

  const handleRegenerateSitemap = () => {
    const project = currentProject;
    if (!project?.id) return;

    setIsRegenerating(true);
    const regenDetails = {
      services: formData.desc_services.trim() || null,
      location: formData.desc_location.trim() || null,
      differentiator: formData.desc_differentiator.trim() || null,
      contact: formData.desc_contact.trim() || null,
      source_url: analyzeUrl.trim() || null,
    };
    const regenHasDetails = Object.values(regenDetails).some(v => v);
    sendRequest(
      {
        url: endPoints.projectGenerateSitemap,
        method: "POST",
        body: {
          project_id: project.id,
          business_description: formData.desc_what.trim() || project.description,
          knowledge_base: formData.knowledge_base || null,
          business_details: regenHasDetails ? regenDetails : null,
          niche: formData.niche || project.niche,
          no_of_pages: formData.no_of_pages || project.no_of_pages,
        },
      },
      (res) => {
        setIsRegenerating(false);
        if (res.status === 200) {
          dispatch(projectActions.setSitemap(res.body));
          setUsedFallback(!!res.used_fallback);
          if (res.used_fallback) {
            console.error("Regenerate AI Error:", res.ai_error);
            const errMsg = res.ai_error?.message || "Unknown error";
            cuteToast({ type: "warning", title: "Still Using Default", message: `AI failed: ${errMsg}. Check your API key in Settings.`, timer: 8000 });
          } else {
            cuteToast({ type: "success", title: "Sitemap Regenerated", message: "New AI-generated sitemap is ready!", timer: 4000 });
          }
        } else {
          cuteToast({ type: "error", message: res.message || "Failed to regenerate sitemap", timer: 4000 });
        }
      }
    );
  };

  const handleAnalyzeUrl = () => {
    if (!analyzeUrl.trim()) {
      setAnalyzeError("Please enter a URL");
      return;
    }

    try {
      new URL(analyzeUrl.trim());
    } catch (e) {
      setAnalyzeError("Please enter a valid URL (starting with http:// or https://)");
      return;
    }

    setIsAnalyzing(true);
    setAnalyzeError(null);
    setLoadingState({
      show: true,
      step: 1,
      message: "Analyzing business URL...",
      subMessage: "Fetching and extracting business information",
      isAnalyzing: true
    });

    sendRequest(
      {
        url: endPoints.projectAnalyzeUrl,
        method: "POST",
        body: { url: analyzeUrl.trim() },
      },
      (response) => {
        setIsAnalyzing(false);
        setLoadingState({ show: false, step: 0, message: "", subMessage: "" });

        if (response.status === 200) {
          const data = response.body;
          setFormData(prev => ({
            ...prev,
            name: data.name || "",
            desc_what: data.desc_what || data.description || "",
            desc_services: data.desc_services || "",
            desc_location: data.desc_location || "",
            desc_differentiator: data.desc_differentiator || "",
            desc_contact: data.desc_contact || "",
            knowledge_base: data.knowledge_base || "",
            niche: data.niche || "general",
            no_of_pages: data.no_of_pages || 3,
          }));

          // Show knowledge base section if AI populated it
          if (data.knowledge_base) setShowKnowledgeBase(true);

          setInputMode("manual");

          cuteToast({
            type: "success",
            title: "AI Auto-Fill Complete",
            message: "Business info extracted! Review and edit the details below.",
            timer: 5000,
          });

          setErrors({});
        } else {
          setAnalyzeError(response.message || "Failed to analyze URL");
          cuteToast({
            type: "error",
            title: "Analysis Failed",
            message: response.message || "Could not analyze the URL",
            timer: 4000,
          });
        }
      }
    );
  };

  const handleCreateAndGenerateSitemap = async () => {
    if (!validateStep1()) return;

    setIsGenerating(true);
    const nicheLabel = NICHES.find(n => n.value === formData.niche)?.label || formData.niche;
    setLoadingState({
      show: true,
      step: 1,
      message: "Creating your project...",
      subMessage: "Setting up your workspace",
      sitemapSteps: [
        { title: "Creating Project", desc: "Setting up your workspace" },
        { title: `Analyzing ${nicheLabel}`, desc: "Understanding industry patterns" },
        { title: `Designing ${formData.no_of_pages} pages`, desc: "Selecting optimal page structure" },
        { title: "Choosing components", desc: "Matching sections to your business" },
        { title: "Building sitemap", desc: "Finalizing your website structure" },
      ]
    });
    dispatch(projectActions.setGenerationStep("generating-sitemap"));

    // Build business details object from guided sub-fields
    const businessDetails = {
      services: formData.desc_services.trim() || null,
      location: formData.desc_location.trim() || null,
      differentiator: formData.desc_differentiator.trim() || null,
      contact: formData.desc_contact.trim() || null,
      source_url: analyzeUrl.trim() || null,
    };
    // Only include if at least one field has data
    const hasDetails = Object.values(businessDetails).some(v => v);

    // Step 1: Create the project
    sendRequest(
      {
        url: endPoints.projectCreate,
        method: "POST",
        body: {
          name: formData.name,
          description: formData.desc_what.trim(),
          niche: formData.niche,
          no_of_pages: formData.no_of_pages,
          theme: formData.theme,
          knowledge_base: formData.knowledge_base || null,
          business_details: hasDetails ? businessDetails : null,
        },
      },
      (response) => {
        if (response.status === 200) {
          const project = response.body;
          dispatch(projectActions.setCurrentProject(project));
          dispatch(projectActions.addProject(project));

          // Update loading state for sitemap generation — start auto-advancing steps
          setLoadingState(prev => ({ ...prev, step: 2, message: "Generating website structure...", subMessage: "AI is analyzing your business and creating optimal pages" }));

          // Auto-advance through steps 3, 4, 5
          const stepTimers = [
            setTimeout(() => setLoadingState(prev => prev.show ? { ...prev, step: 3 } : prev), 4000),
            setTimeout(() => setLoadingState(prev => prev.show ? { ...prev, step: 4 } : prev), 8000),
            setTimeout(() => setLoadingState(prev => prev.show ? { ...prev, step: 5 } : prev), 12000),
          ];

          // Step 2: Generate sitemap
          sendRequest(
            {
              url: endPoints.projectGenerateSitemap,
              method: "POST",
              body: {
                project_id: project.id,
                business_description: formData.desc_what.trim(),
                knowledge_base: formData.knowledge_base || null,
                business_details: hasDetails ? businessDetails : null,
                niche: formData.niche,
                no_of_pages: formData.no_of_pages,
              },
            },
            (sitemapResponse) => {
              stepTimers.forEach(clearTimeout);
              setIsGenerating(false);
              setLoadingState({ show: false, step: 0, message: "", subMessage: "" });

              if (sitemapResponse.status === 200) {
                dispatch(projectActions.setSitemap(sitemapResponse.body));
                dispatch(projectActions.setGenerationStep("idle"));

                // Redirect to the SitemapEditor page instead of showing inline step 2
                history.push(`/builder/sitemap/${project.id}`);

                // Track fallback usage
                if (sitemapResponse.used_fallback) {
                  setUsedFallback(true);
                  console.error("Sitemap AI Error:", sitemapResponse.ai_error);
                  const errMsg = sitemapResponse.ai_error?.message || "Unknown error";
                  cuteToast({
                    type: "warning",
                    title: "Using Default Structure",
                    message: `AI failed: ${errMsg}. Using default template — you can customize or regenerate.`,
                    timer: 8000,
                  });
                } else {
                  setUsedFallback(false);
                  cuteToast({
                    type: "success",
                    title: "Sitemap Generated",
                    message: "AI has created your website structure. You can now customize it.",
                    timer: 4000,
                  });
                }
              } else {
                dispatch(projectActions.setGenerationStep("idle"));
                cuteToast({
                  type: "error",
                  title: "Generation Failed",
                  message: sitemapResponse.message || "Failed to generate sitemap",
                  timer: 4000,
                });
              }
            }
          );
        } else {
          setIsGenerating(false);
          setLoadingState({ show: false, step: 0, message: "", subMessage: "" });
          dispatch(projectActions.setGenerationStep("idle"));
          cuteToast({
            type: "error",
            title: "Error",
            message: response.message || "Failed to create project",
            timer: 4000,
          });
        }
      }
    );
  };

  const handleGenerateContent = async () => {
    if (!currentProject?.id) return;

    // Calculate total sections (excluding headers/footers)
    const totalSections = sitemap?.pages?.reduce((acc, page) =>
      acc + (page.sections?.filter(s => !/HEADER|FOOTER/i.test(s.component_code)).length || 0), 0) || 0;

    setIsGenerating(true);
    setLoadingState({
      show: true,
      step: 1,
      message: "Generating website content...",
      subMessage: `Preparing to generate ${totalSections} sections...`,
      totalSections,
      completedSections: 0,
      isContentGeneration: true
    });
    dispatch(projectActions.setGenerationStep("generating-content"));

    try {
      const response = await fetch(endPoints.projectGenerateContentStream, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token
        },
        body: JSON.stringify({ project_id: currentProject.id })
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop(); // keep incomplete chunk

        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith('data: ')) continue;

          try {
            const data = JSON.parse(line.slice(6));

            if (data.type === 'progress') {
              setLoadingState(prev => ({
                ...prev,
                completedSections: data.completed,
                subMessage: data.completed === 0
                  ? `Preparing to generate ${data.total} sections...`
                  : `Section ${data.completed} of ${data.total}: ${data.current_section}`
              }));
            } else if (data.type === 'complete') {
              setIsGenerating(false);
              setLoadingState({
                show: true,
                step: 2,
                message: "Content generated successfully!",
                subMessage: "Preparing your editor...",
                isComplete: true
              });

              dispatch(projectActions.setWireframes(data.wireframes));
              dispatch(projectActions.setGenerationStep("complete"));

              cuteToast({
                type: "success",
                title: "Content Generated",
                message: "AI has generated content for all sections. Redirecting to editor...",
                timer: 4000,
              });

              setTimeout(() => {
                setLoadingState({ show: false, step: 0, message: "", subMessage: "" });
                history.push(`/builder/edit/${currentProject.id}`);
              }, 1500);
            } else if (data.type === 'error') {
              throw new Error(data.message);
            }
          } catch (parseErr) {
            if (parseErr.message !== 'Unexpected end of JSON input') {
              throw parseErr;
            }
          }
        }
      }
    } catch (error) {
      setIsGenerating(false);
      dispatch(projectActions.setGenerationStep("idle"));
      setLoadingState({
        show: true,
        step: 0,
        message: "Content Generation Failed",
        subMessage: error.message || "An error occurred while generating content. You can retry or go back to edit your sitemap.",
        isError: true,
        isContentGeneration: true
      });
    }
  };

  const handleSitemapUpdate = (updatedSitemap) => {
    dispatch(projectActions.setSitemap(updatedSitemap));
    // Save sitemap to backend
    if (currentProject?.id) {
      sendRequest(
        {
          url: `${endPoints.projects}/${currentProject.id}/sitemap/update`,
          method: "POST",
          body: { sitemap_json: JSON.stringify(updatedSitemap) },
        },
        () => {}
      );
    }
  };

  // Generate sections for a new page using AI
  const handleGeneratePage = async (pageName, pageDescription) => {
    if (!currentProject?.id) return null;

    return new Promise((resolve) => {
      sendRequest(
        {
          url: `${endPoints.projects}/${currentProject.id}/sitemap/generate-page`,
          method: "POST",
          body: { page_name: pageName, page_description: pageDescription },
        },
        (response) => {
          if (response.status === 200) {
            cuteToast({
              type: "success",
              title: "Page Created",
              message: `"${pageName}" page has been added to your sitemap`,
              timer: 3000,
            });
            resolve(response.body);
          } else {
            cuteToast({
              type: "error",
              title: "Failed",
              message: response.message || "Failed to generate page",
              timer: 4000,
            });
            resolve(null);
          }
        }
      );
    });
  };

  // Get the selected theme object for preview
  const selectedTheme = THEMES[formData.theme] || THEMES.classic;

  // Full-screen light builder layout (Bland Design System)
  return (
    <div className="pc-container">
      {/* Header Toolbar */}
      <header className="pc-toolbar">
        <div className="pc-toolbar-left">
          <Link to="/" className="pc-toolbar-logo">
            <img src={endPoints.logo} alt="Logo" />
          </Link>

          <div className="pc-toolbar-divider"></div>

          <Link to="/" className="pc-toolbar-back">
            <FiArrowLeft size={14} /> Back
          </Link>

          <div className="pc-toolbar-divider"></div>

          <div>
            <div className="pc-toolbar-title">
              {step === 1 ? "New Project" : (currentProject?.name || "Website Structure")}
            </div>
            <div className="pc-toolbar-subtitle">
              {step === 1 ? "Configure & generate" : "Design your sitemap"}
            </div>
          </div>
        </div>

        {/* Regenerate button (step 2, fallback only) */}
        {step === 2 && usedFallback && (
          <button
            className="pc-regen-btn"
            onClick={handleRegenerateSitemap}
            disabled={isRegenerating}
          >
            {isRegenerating ? <FiLoader size={13} style={{ animation: "spin 1s linear infinite" }} /> : <FiZap size={13} />}
            {isRegenerating ? "Regenerating..." : "Regenerate Sitemap"}
          </button>
        )}

        {/* View Mode Toggle (step 2 only) */}
        {step === 2 && (
          <div className="pc-view-toggle">
            <button
              onClick={() => setViewMode("text")}
              className={`pc-view-btn ${viewMode === "text" ? "pc-view-btn--active-text" : ""}`}
            >
              <FiAlignLeft size={14} /> Text
            </button>
            <button
              onClick={() => setViewMode("visual")}
              className={`pc-view-btn ${viewMode === "visual" ? "pc-view-btn--active-visual" : ""}`}
            >
              <FiImage size={14} /> Visual
            </button>
          </div>
        )}

        <div>
          {step === 2 && (
            <button
              className="pc-content-btn"
              onClick={handleGenerateContent}
              disabled={isGenerating}
            >
              {isGenerating ? (
                <><FiLoader style={{ animation: "spin 1s linear infinite" }} /> Generating...</>
              ) : (
                <>Generate Content <FiArrowRight size={14} /></>
              )}
            </button>
          )}
          {step === 1 && <div style={{ width: "120px" }}></div>}
        </div>
      </header>

      {/* Main Content Area */}
      <div className="pc-main">
        {step === 1 ? (
          <div style={{ flex: 1, overflow: "auto" }}>
            <div className="pc-page-wrap">
              <h1 className="pc-heading">Create your <span>website</span></h1>

              {/* Mode Toggle */}
              <div className="pc-mode-bar">
                <button
                  type="button"
                  className={`pc-mode-btn ${inputMode === 'manual' ? 'active' : ''}`}
                  onClick={() => setInputMode('manual')}
                >
                  <FiEdit3 size={13} /> Manual
                </button>
                <button
                  type="button"
                  className={`pc-mode-btn ${inputMode === 'ai-autofill' ? 'active' : ''}`}
                  onClick={() => { setInputMode('ai-autofill'); setAnalyzeError(null); }}
                >
                  <FiGlobe size={13} /> Auto-Fill from URL
                </button>
              </div>

              {/* AI Auto-Fill */}
              {inputMode === "ai-autofill" && (
                <div style={{ animation: "pc-fadeIn 0.3s ease", marginBottom: "24px" }}>
                  <div className="pc-field">
                    <label className="pc-label">Business URL</label>
                    <input
                      type="url"
                      className="pc-input"
                      value={analyzeUrl}
                      onChange={(e) => {
                        setAnalyzeUrl(e.target.value);
                        if (analyzeError) setAnalyzeError(null);
                      }}
                      placeholder="Paste a Google Maps link or website URL..."
                      style={{ borderColor: analyzeError ? "#ef4444" : undefined }}
                    />
                    {analyzeError && (
                      <div className="pc-error"><span>!</span> {analyzeError}</div>
                    )}
                    <div className="pc-hint">AI will extract business name, description, and category</div>
                  </div>
                  <button
                    className="pc-generate-btn full-width"
                    onClick={handleAnalyzeUrl}
                    disabled={isAnalyzing || !analyzeUrl.trim()}
                  >
                    <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", position: "relative", zIndex: 1 }}>
                      {isAnalyzing ? (
                        <><FiLoader style={{ animation: "spin 1s linear infinite" }} /> Analyzing...</>
                      ) : (
                        <><FiGlobe size={16} /> Analyze & Auto-Fill</>
                      )}
                    </span>
                  </button>
                </div>
              )}

              {/* Manual Form */}
              {inputMode === "manual" && (<>
                {/* Row: Name / Type / Pages */}
                <div className="pc-fields-row">
                  <div className="pc-field">
                    <label className="pc-label">Project Name</label>
                    <input
                      type="text"
                      className="pc-input"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="e.g., Joe's Pizza Shop"
                      style={{ borderColor: errors.name ? "#ef4444" : undefined }}
                    />
                    {errors.name && <div className="pc-error">{errors.name}</div>}
                  </div>

                  <div className="pc-field" style={{ position: "relative" }}>
                    <label className="pc-label">Business Type</label>
                    <button
                      type="button"
                      className="pc-select"
                      onClick={() => { setNicheDropdownOpen(!nicheDropdownOpen); setNicheSearch(""); }}
                      style={{ textAlign: "left", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between" }}
                    >
                      <span>{NICHES.find(n => n.value === formData.niche)?.label || "Select..."}</span>
                      <FiChevronDown size={14} style={{ color: "var(--bland-purple-500)", transition: "transform 0.2s", transform: nicheDropdownOpen ? "rotate(180deg)" : "none" }} />
                    </button>
                    {nicheDropdownOpen && (
                      <div className="pc-niche-dropdown">
                        <div className="pc-niche-search-wrap">
                          <FiSearch size={13} style={{ color: "var(--bland-gray-400)", flexShrink: 0 }} />
                          <input
                            className="pc-niche-search"
                            placeholder="Search business type..."
                            value={nicheSearch}
                            onChange={(e) => setNicheSearch(e.target.value)}
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === "Escape") setNicheDropdownOpen(false);
                            }}
                          />
                        </div>
                        <div className="pc-niche-list">
                          {NICHE_GROUPS.map((group) => {
                            const filtered = group.niches.filter(n =>
                              n.label.toLowerCase().includes(nicheSearch.toLowerCase()) ||
                              n.value.toLowerCase().includes(nicheSearch.toLowerCase())
                            );
                            if (filtered.length === 0) return null;
                            return (
                              <div key={group.label}>
                                <div className="pc-niche-group-label">{group.label}</div>
                                {filtered.map((niche) => (
                                  <button
                                    key={niche.value}
                                    type="button"
                                    className={`pc-niche-option ${formData.niche === niche.value ? 'selected' : ''}`}
                                    onClick={() => {
                                      setFormData(prev => ({ ...prev, niche: niche.value }));
                                      setNicheDropdownOpen(false);
                                      setNicheSearch("");
                                    }}
                                  >
                                    {niche.label}
                                    {formData.niche === niche.value && <FiCheck size={13} style={{ color: "var(--bland-purple-500)" }} />}
                                  </button>
                                ))}
                              </div>
                            );
                          })}
                          {NICHE_GROUPS.every(g => g.niches.every(n =>
                            !n.label.toLowerCase().includes(nicheSearch.toLowerCase()) &&
                            !n.value.toLowerCase().includes(nicheSearch.toLowerCase())
                          )) && (
                            <div style={{ padding: "12px 16px", color: "var(--bland-gray-400)", fontSize: "13px", fontFamily: "'DM Sans', sans-serif" }}>
                              No matches found
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="pc-field">
                    <label className="pc-label">Pages</label>
                    <div className="pc-pages-wrap">
                      {[1, 2, 3, 4, 5].map(num => (
                        <button
                          key={num}
                          type="button"
                          className={`pc-page-btn ${formData.no_of_pages === num ? 'active' : ''}`}
                          onClick={() => setFormData(prev => ({ ...prev, no_of_pages: num }))}
                        >
                          {num}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Dynamic page name preview */}
                <div className="pc-page-preview">
                  {(PAGE_NAMES_BY_NICHE[formData.niche]?.[formData.no_of_pages] ||
                    PAGE_NAMES_BY_NICHE.general[formData.no_of_pages]).map((name, i) => (
                    <span key={i} className="pc-page-pill">
                      {name}
                    </span>
                  ))}
                </div>

                {/* Theme selector: 5 cards with preview images */}
                <div className="pc-field" style={{ marginBottom: "24px" }}>
                  <label className="pc-label">Visual Theme</label>
                  <div className="pc-themes-grid">
                    {Object.values(THEMES).map(theme => {
                      const isSelected = formData.theme === theme.id;
                      return (
                        <button
                          key={theme.id}
                          type="button"
                          className={`pc-theme-card ${isSelected ? 'active' : ''}`}
                          onClick={() => setFormData(prev => ({ ...prev, theme: theme.id }))}
                          style={{
                            borderColor: isSelected ? theme.defaultStyleGuide.colors.primary : undefined,
                            boxShadow: isSelected ? `0 0 20px ${theme.defaultStyleGuide.colors.primary}30` : undefined
                          }}
                        >
                          <div className="pc-theme-card-img">
                            <img src={theme.preview_image} alt={theme.name} />
                            <div className="pc-theme-card-dots">
                              <span className="pc-theme-card-dot" style={{ background: theme.defaultStyleGuide.colors.primary }}></span>
                              <span className="pc-theme-card-dot" style={{ background: theme.defaultStyleGuide.colors.secondary }}></span>
                              <span className="pc-theme-card-dot" style={{ background: theme.defaultStyleGuide.colors.background }}></span>
                            </div>
                            <button
                              type="button"
                              className="pc-theme-preview-btn"
                              onClick={(e) => { e.stopPropagation(); setThemePreview(theme); }}
                              title="Preview theme"
                            >
                              <FiEye size={14} />
                            </button>
                          </div>
                          <div className="pc-theme-card-body">
                            <div className="pc-theme-card-name">
                              {theme.name}
                              {theme.recommended_for?.includes(formData.niche) && (
                                <span className="pc-theme-rec-badge">Recommended</span>
                              )}
                            </div>
                            {theme.best_for_label && (
                              <div className="pc-theme-card-desc">{theme.best_for_label}</div>
                            )}
                          </div>
                          {isSelected && (
                            <span className="pc-theme-check" style={{ background: theme.defaultStyleGuide.colors.primary }}>
                              <FiCheck size={9} color="#fff" />
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Guided Description Sub-Fields */}
                <div className="pc-guided-fields">
                  <label className="pc-label" style={{ marginBottom: "12px" }}>Describe Your Business</label>

                  <div className="pc-guided-grid">
                    <div className="pc-field">
                      <label className="pc-label" style={{ fontSize: "10px", color: "var(--bland-gray-600)" }}>What does your business do? *</label>
                      <textarea
                        className="pc-textarea"
                        name="desc_what"
                        value={formData.desc_what}
                        onChange={handleInputChange}
                        placeholder="e.g., Family-owned Italian restaurant serving authentic Neapolitan cuisine..."
                        rows={2}
                        style={{ minHeight: "60px", borderColor: errors.desc_what ? "#ef4444" : undefined }}
                      />
                      {errors.desc_what && <div className="pc-error">{errors.desc_what}</div>}
                    </div>

                    <div className="pc-field">
                      <label className="pc-label" style={{ fontSize: "10px", color: "var(--bland-gray-600)" }}>Services / Products Offered</label>
                      <textarea
                        className="pc-textarea"
                        name="desc_services"
                        value={formData.desc_services}
                        onChange={handleInputChange}
                        placeholder="e.g., Wood-fired pizza, fresh pasta, catering for events, private dining..."
                        rows={2}
                        style={{ minHeight: "60px" }}
                      />
                    </div>

                    <div className="pc-field">
                      <label className="pc-label" style={{ fontSize: "10px", color: "var(--bland-gray-600)" }}>Location & Service Area</label>
                      <input
                        className="pc-input"
                        name="desc_location"
                        value={formData.desc_location}
                        onChange={handleInputChange}
                        placeholder="e.g., Downtown Austin, TX — serving all of Travis County"
                      />
                    </div>

                    <div className="pc-field">
                      <label className="pc-label" style={{ fontSize: "10px", color: "var(--bland-gray-600)" }}>What makes you different?</label>
                      <textarea
                        className="pc-textarea"
                        name="desc_differentiator"
                        value={formData.desc_differentiator}
                        onChange={handleInputChange}
                        placeholder="e.g., 20 years experience, imported ingredients from Italy, 4.9★ Google rating..."
                        rows={2}
                        style={{ minHeight: "60px" }}
                      />
                    </div>

                    <div className="pc-field" style={{ gridColumn: "1 / -1" }}>
                      <label className="pc-label" style={{ fontSize: "10px", color: "var(--bland-gray-600)" }}>Contact Details</label>
                      <input
                        className="pc-input"
                        name="desc_contact"
                        value={formData.desc_contact}
                        onChange={handleInputChange}
                        placeholder="e.g., (512) 555-1234, info@business.com, facebook.com/mybusiness, @mybusiness"
                      />
                    </div>
                  </div>

                  {/* Quality indicator */}
                  {(() => {
                    const len = mergeDescription().length;
                    const kbLen = formData.knowledge_base.length;
                    const total = len + kbLen;
                    const label = total > 500 ? "Excellent detail" : total > 200 ? "Good detail" : total > 50 ? "Basic — add more for better results" : "";
                    const color = total > 500 ? "#059669" : total > 200 ? "var(--bland-purple-500)" : "var(--bland-gray-400)";
                    return label ? (
                      <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "10px", color, marginTop: "8px" }}>
                        {label} ({total} chars)
                      </div>
                    ) : null;
                  })()}

                  {/* AI Enhance + Knowledge Base */}
                  <div style={{ marginTop: "16px", paddingTop: "16px", borderTop: "1px solid var(--bland-gray-100)", display: "flex", alignItems: "center", gap: "14px", flexWrap: "wrap" }}>
                    <button
                      type="button"
                      className="pc-enhance-btn"
                      onClick={handleEnhanceDescription}
                      disabled={isEnhancing || mergeDescription().length < 30}
                      style={{ opacity: mergeDescription().length < 30 ? 0.4 : 1 }}
                    >
                      {isEnhancing ? <FiLoader style={{ animation: "spin 1s linear infinite" }} size={11} /> : <FiZap size={11} />}
                      {isEnhancing ? "Enhancing..." : "AI Enhance"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowKnowledgeBase(!showKnowledgeBase)}
                      style={{
                        background: "none", border: "none", cursor: "pointer",
                        fontFamily: "'DM Sans', sans-serif", fontSize: "12px", fontWeight: 600,
                        color: formData.knowledge_base ? "var(--bland-purple-600)" : "var(--bland-gray-500)",
                        padding: 0, display: "flex", alignItems: "center", gap: "5px"
                      }}
                    >
                      {showKnowledgeBase ? "▾" : "▸"} Extra Context / Knowledge Base
                      {formData.knowledge_base && <span style={{ color: "var(--bland-success)", fontSize: "11px" }}>({formData.knowledge_base.length} chars)</span>}
                    </button>
                  </div>
                  {showKnowledgeBase && (
                    <div style={{ marginTop: "8px", animation: "pc-fadeIn 0.3s ease" }}>
                      <textarea
                        className="pc-textarea"
                        name="knowledge_base"
                        value={formData.knowledge_base}
                        onChange={handleInputChange}
                        placeholder="Detailed business info — reviews, team details, pricing, history, certifications. AI uses this to generate richer, more accurate content..."
                        rows={5}
                        style={{ minHeight: "120px" }}
                      />
                      <div className="pc-hint">Optional — auto-filled by URL analysis or AI Enhance. Provides deeper context for better content generation.</div>
                    </div>
                  )}
                </div>

                {/* Pre-generation summary */}
                {mergeDescription().length > 50 && (
                  <div style={{
                    marginTop: "16px", padding: "12px 16px", borderRadius: "10px",
                    background: "var(--bland-purple-50)", border: "1px solid var(--bland-purple-100)",
                    fontFamily: "'DM Sans', sans-serif", fontSize: "13px", color: "var(--bland-gray-600)"
                  }}>
                    Your website plan: <strong style={{ color: "var(--bland-gray-900)" }}>{formData.no_of_pages}-page {THEMES[formData.theme]?.name || 'Evergreen'}</strong> website
                    {" — "}
                    {(PAGE_NAMES_BY_NICHE[formData.niche]?.[formData.no_of_pages] ||
                      PAGE_NAMES_BY_NICHE.general[formData.no_of_pages]).join(" / ")}
                    {formData.knowledge_base && <span style={{ color: "var(--bland-success)" }}> + detailed context</span>}
                  </div>
                )}

                {/* Generate button */}
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "16px" }}>
                  <button
                    className="pc-generate-btn"
                    onClick={handleCreateAndGenerateSitemap}
                    disabled={isGenerating}
                  >
                    <span style={{ display: "flex", alignItems: "center", gap: "8px", position: "relative", zIndex: 1 }}>
                      {isGenerating ? (
                        <><FiLoader style={{ animation: "spin 1s linear infinite" }} /> Generating...</>
                      ) : (
                        <><FiZap size={16} /> Generate</>
                      )}
                    </span>
                  </button>
                </div>
              </>)}
            </div>
          </div>
        ) : (
          // Step 2: Sitemap Canvas (full width)
          <div style={{
            flex: 1,
            overflow: "auto",
            backgroundColor: "var(--bland-gray-50)",
            backgroundImage: `
              linear-gradient(var(--bland-gray-200) 1px, transparent 1px),
              linear-gradient(90deg, var(--bland-gray-200) 1px, transparent 1px)
            `,
            backgroundSize: "40px 40px"
          }}>
          <SitemapCanvas
            sitemap={sitemap}
            onUpdate={handleSitemapUpdate}
            niche={currentProject?.niche || formData.niche}
            darkMode={true}
            showZoomControls={true}
            projectId={currentProject?.id}
            onGeneratePage={handleGeneratePage}
            viewMode={viewMode}
            projectTheme={currentProject?.theme || formData.theme || "classic"}
          />
          </div>
        )}
      </div>

      {/* Full-screen Loading Overlay */}
      {/* Theme Preview Lightbox */}
      {themePreview && (
        <div className="pc-theme-lightbox" onClick={() => setThemePreview(null)}>
          <div className="pc-theme-lightbox-inner" onClick={(e) => e.stopPropagation()}>
            <img src={themePreview.preview_image} alt={themePreview.name} />
            <button className="pc-theme-lightbox-close" onClick={() => setThemePreview(null)}>
              <FiX size={16} />
            </button>
            <div className="pc-theme-lightbox-info">
              <strong>{themePreview.name}</strong> — {themePreview.description}
            </div>
          </div>
        </div>
      )}

      {loadingState.show && (
        <div className="generation-overlay">
          <style>{`
            .generation-overlay {
              position: fixed;
              inset: 0;
              background: rgba(17, 24, 39, 0.4);
              backdrop-filter: blur(16px);
              display: flex;
              align-items: center;
              justify-content: center;
              z-index: 10000;
              padding: 24px;
              animation: genFadeIn 0.3s ease;
            }

            @keyframes genFadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }

            .generation-content {
              text-align: center;
              max-width: 460px;
              width: 100%;
              max-height: calc(100vh - 48px);
              overflow-y: auto;
              padding: 32px 32px 28px;
              background: var(--bland-white, #fff);
              border-radius: 18px;
              box-shadow: 0 24px 48px -12px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.03);
              animation: genSlideUp 0.35s cubic-bezier(0.16, 1, 0.3, 1);
            }

            .generation-content::-webkit-scrollbar { width: 4px; }
            .generation-content::-webkit-scrollbar-track { background: transparent; }
            .generation-content::-webkit-scrollbar-thumb { background: var(--bland-gray-200, #E5E7EB); border-radius: 4px; }

            @keyframes genSlideUp {
              from { opacity: 0; transform: translateY(12px); }
              to { opacity: 1; transform: translateY(0); }
            }

            /* Compact header: spinner + text side by side */
            .generation-header-row {
              display: flex;
              align-items: center;
              gap: 18px;
              margin-bottom: 24px;
              text-align: left;
            }

            .generation-spinner {
              width: 52px;
              height: 52px;
              position: relative;
              flex-shrink: 0;
            }

            .generation-spinner::before,
            .generation-spinner::after {
              content: '';
              position: absolute;
              border-radius: 50%;
            }

            .generation-spinner::before {
              inset: 0;
              border: 3px solid var(--bland-gray-100, #F3F4F6);
            }

            .generation-spinner::after {
              inset: 0;
              border: 3px solid transparent;
              border-top-color: var(--bland-purple-500, #8B5CF6);
              animation: genSpin 0.9s linear infinite;
            }

            @keyframes genSpin {
              to { transform: rotate(360deg); }
            }

            .generation-icon {
              position: absolute;
              inset: 0;
              display: flex;
              align-items: center;
              justify-content: center;
              color: var(--bland-purple-500, #8B5CF6);
              animation: genPulse 2s ease-in-out infinite;
            }

            @keyframes genPulse {
              0%, 100% { transform: scale(1); opacity: 1; }
              50% { transform: scale(1.06); opacity: 0.7; }
            }

            .generation-header-text {
              flex: 1;
              min-width: 0;
            }

            .generation-title {
              font-family: 'Plus Jakarta Sans', -apple-system, sans-serif;
              font-size: 18px;
              font-weight: 800;
              color: var(--bland-gray-900, #111827);
              margin: 0 0 4px;
              letter-spacing: -0.3px;
              line-height: 1.3;
            }

            .generation-subtitle {
              font-family: 'DM Sans', sans-serif;
              font-size: 14px;
              color: var(--bland-gray-500, #6B7280);
              margin: 0;
              line-height: 1.5;
            }

            .generation-steps {
              display: flex;
              flex-direction: column;
              gap: 8px;
              text-align: left;
            }

            .generation-step {
              display: flex;
              align-items: center;
              gap: 12px;
              padding: 12px 16px;
              background: var(--bland-gray-50, #F9FAFB);
              border: 1px solid var(--bland-gray-200, #E5E7EB);
              border-radius: 10px;
              transition: all 0.25s ease;
            }

            .generation-step.active {
              background: var(--bland-purple-50, #F5F3FF);
              border-color: var(--bland-purple-200, #DDD6FE);
            }

            .generation-step.completed {
              background: #ECFDF5;
              border-color: #A7F3D0;
            }

            .step-indicator {
              width: 28px;
              height: 28px;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 12px;
              font-weight: 700;
              flex-shrink: 0;
            }

            .step-indicator.pending {
              background: var(--bland-gray-100, #F3F4F6);
              color: var(--bland-gray-400, #9CA3AF);
            }

            .step-indicator.active {
              background: var(--bland-purple-100, #EDE9FE);
              color: var(--bland-purple-600, #7C3AED);
              animation: genPulse 1.5s ease-in-out infinite;
            }

            .step-indicator.completed {
              background: #D1FAE5;
              color: #059669;
            }

            .step-text {
              flex: 1;
              min-width: 0;
            }

            .step-title {
              font-family: 'DM Sans', sans-serif;
              font-size: 14px;
              font-weight: 600;
              color: var(--bland-gray-800, #1F2937);
              margin-bottom: 1px;
            }

            .step-description {
              font-family: 'DM Sans', sans-serif;
              font-size: 13px;
              color: var(--bland-gray-400, #9CA3AF);
            }

            .pc-progress-bar-wrap {
              margin-top: 18px;
              width: 100%;
            }
            .pc-progress-bar {
              width: 100%;
              height: 5px;
              background: var(--bland-gray-100, #F3F4F6);
              border-radius: 5px;
              overflow: hidden;
            }
            .pc-progress-bar-fill {
              height: 100%;
              background: linear-gradient(90deg, var(--bland-purple-400, #A78BFA), var(--bland-purple-600, #7C3AED));
              border-radius: 5px;
              transition: width 0.4s cubic-bezier(0.16, 1, 0.3, 1);
            }
            .pc-progress-label {
              margin-top: 8px;
              font-size: 13px;
              color: var(--bland-gray-400, #9CA3AF);
              text-align: center;
              font-weight: 500;
            }
            .step-spinner {
              width: 16px;
              height: 16px;
              border: 2px solid var(--bland-purple-200, #DDD6FE);
              border-top-color: var(--bland-purple-600, #7C3AED);
              border-radius: 50%;
              animation: genSpin 0.8s linear infinite;
            }

            .generation-timer {
              margin-top: 22px;
              font-family: 'DM Sans', sans-serif;
              font-size: 13px;
              color: var(--bland-gray-400, #9CA3AF);
            }

            .dot-animation::after {
              content: '';
              animation: genDots 1.5s infinite;
            }

            @keyframes genDots {
              0%, 20% { content: ''; }
              40% { content: '.'; }
              60% { content: '..'; }
              80%, 100% { content: '...'; }
            }
          `}</style>

          <div className="generation-content">
            <div className="generation-header-row">
              <div className="generation-spinner">
                <div className="generation-icon">
                  {loadingState.isError ? (
                    <FiX size={22} style={{ color: 'var(--bland-error, #EF4444)' }} />
                  ) : loadingState.isComplete ? (
                    <FiCheck size={22} />
                  ) : (
                    <FiZap size={20} />
                  )}
                </div>
              </div>
              <div className="generation-header-text">
                <h2 className="generation-title">{loadingState.message}</h2>
                {!loadingState.isError && (
                  <p className="generation-subtitle">{loadingState.subMessage}</p>
                )}
              </div>
            </div>

            {loadingState.isAnalyzing ? (
              <div className="generation-steps">
                <div className="generation-step active">
                  <div className="step-indicator active">1</div>
                  <div className="step-text">
                    <div className="step-title">Fetching URL Content</div>
                    <div className="step-description">Downloading and parsing the webpage</div>
                  </div>
                </div>
                <div className="generation-step">
                  <div className="step-indicator pending">2</div>
                  <div className="step-text">
                    <div className="step-title">AI Analysis</div>
                    <div className="step-description">Extracting business name, description, reviews, and category</div>
                  </div>
                </div>
              </div>
            ) : loadingState.isContentGeneration ? (
              <div className="generation-steps">
                {loadingState.isError ? (
                  <>
                    <div className="generation-step" style={{ background: '#FEF2F2', borderColor: '#FCA5A5' }}>
                      <div className="step-indicator" style={{ background: '#FEE2E2', color: '#DC2626' }}>!</div>
                      <div className="step-text">
                        <div className="step-title">Generation Failed</div>
                        <div className="step-description">{loadingState.subMessage}</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '10px', marginTop: '18px', justifyContent: 'center' }}>
                      <button
                        onClick={() => {
                          setLoadingState({ show: false, step: 0, message: "", subMessage: "" });
                        }}
                        style={{
                          padding: '10px 22px', borderRadius: '8px', border: '1px solid var(--bland-gray-200, #E5E7EB)',
                          background: 'var(--bland-white, #fff)', color: 'var(--bland-gray-600, #4B5563)',
                          cursor: 'pointer', fontSize: '14px', fontWeight: '500',
                          fontFamily: "'DM Sans', sans-serif", display: 'flex', alignItems: 'center'
                        }}
                      >
                        <FiArrowLeft size={14} style={{ marginRight: '6px' }} /> Back to Sitemap
                      </button>
                      <button
                        onClick={handleGenerateContent}
                        style={{
                          padding: '10px 22px', borderRadius: '8px', border: 'none',
                          background: 'var(--bland-purple-600, #7C3AED)', color: '#fff',
                          cursor: 'pointer', fontSize: '14px', fontWeight: '600',
                          fontFamily: "'DM Sans', sans-serif", display: 'flex', alignItems: 'center',
                          boxShadow: '0 1px 3px rgba(124, 58, 237, 0.2)'
                        }}
                      >
                        <FiZap size={14} style={{ marginRight: '6px' }} /> Retry Generation
                      </button>
                    </div>
                  </>
                ) : loadingState.isComplete ? (
                  <>
                    <div className="generation-step completed">
                      <div className="step-indicator completed"><FiCheck size={16} /></div>
                      <div className="step-text">
                        <div className="step-title">Content Generated</div>
                        <div className="step-description">All sections created successfully</div>
                      </div>
                    </div>
                    <div className="generation-step completed">
                      <div className="step-indicator completed"><FiCheck size={16} /></div>
                      <div className="step-text">
                        <div className="step-title">Preparing Editor</div>
                        <div className="step-description">Setting up your visual editor</div>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="generation-step active">
                      <div className="step-indicator active">
                        <div className="step-spinner"></div>
                      </div>
                      <div className="step-text">
                        <div className="step-title">Generating Content</div>
                        <div className="step-description">{loadingState.subMessage}</div>
                      </div>
                    </div>
                    {loadingState.totalSections > 0 && (
                      <div className="pc-progress-bar-wrap">
                        <div className="pc-progress-bar">
                          <div
                            className="pc-progress-bar-fill"
                            style={{ width: `${Math.round(((loadingState.completedSections || 0) / loadingState.totalSections) * 100)}%` }}
                          ></div>
                        </div>
                        <div className="pc-progress-label">
                          {loadingState.completedSections || 0} / {loadingState.totalSections} sections
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : (
              <div className="generation-steps">
                {(loadingState.sitemapSteps || [
                  { title: "Creating Project", desc: "Setting up your workspace" },
                  { title: "Generating Sitemap", desc: "AI is designing optimal page structure" },
                ]).map((s, i) => {
                  const stepNum = i + 1;
                  const isCompleted = loadingState.step > stepNum;
                  const isActive = loadingState.step === stepNum;
                  return (
                    <div key={i} className={`generation-step ${isCompleted ? 'completed' : isActive ? 'active' : ''}`}>
                      <div className={`step-indicator ${isCompleted ? 'completed' : isActive ? 'active' : 'pending'}`}>
                        {isCompleted ? <FiCheck size={16} /> : stepNum}
                      </div>
                      <div className="step-text">
                        <div className="step-title">{s.title}</div>
                        <div className="step-description">{s.desc}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {!loadingState.isError && !loadingState.isComplete && (
              <div className="generation-timer">
                {loadingState.isAnalyzing
                  ? "This usually takes 10-20 seconds"
                  : loadingState.isContentGeneration
                    ? "This may take 1-2 minutes depending on the number of sections"
                    : "This usually takes 10-20 seconds"
                }<span className="dot-animation"></span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectCreate;
